import React, { useCallback, useEffect, useState } from 'react';
import { Radio, Copy, Check, RefreshCw, Loader2, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** Personal podcast feed URL for paying subscribers. The URL is tied to the
 *  account and stops serving audio the moment the subscription lapses. */
const PodcastFeedCard = () => {
  const [state, setState] = useState({ status: 'loading' });
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/me/podcast`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) {
        setState({ status: 'locked', detail: data.detail || 'Subscriber benefit.' });
        return;
      }
      setState({ status: 'ready', ...data });
    } catch (e) {
      setState({ status: 'locked', detail: e.message });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(state.feed_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard blocked - user can select manually */ }
  };

  const rotate = async () => {
    if (!window.confirm('Issue a new feed URL? The old one stops working immediately.')) return;
    setRotating(true);
    try {
      const res = await fetch(`${API}/api/me/podcast/rotate`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (res.ok) setState((s) => ({ ...s, feed_url: data.feed_url }));
    } finally {
      setRotating(false);
    }
  };

  if (state.status === 'loading' || state.status === 'locked') return null;

  const appleUrl = `https://podcasts.apple.com/podcast?url=${encodeURIComponent(state.feed_url)}`;
  const overcastUrl = `https://overcast.fm/add?url=${encodeURIComponent(state.feed_url)}`;

  return (
    <div
      className="border border-red-900/40 rounded-lg bg-gradient-to-br from-red-950/25 via-zinc-950 to-black p-5 mb-6"
      data-testid="podcast-feed-card"
    >
      <div className="flex items-start gap-3">
        <Radio className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-black uppercase tracking-[0.25em] text-red-400">Private podcast feed</p>
          <h3 className="text-base md:text-lg font-black text-white mt-1">Listen in your podcast app</h3>
          <p className="text-sm text-zinc-400 leading-snug mt-1">
            Every investigation, narrated and scored with our newsroom bed. Paste this URL into Apple
            Podcasts, Overcast, Pocket Casts or any app that takes a feed.
            {typeof state.episodes === 'number' && (
              <span className="text-zinc-500"> {state.episodes} episodes ready.</span>
            )}
          </p>

          <div className="mt-3 flex items-center gap-2 rounded border border-zinc-800 bg-black/60 px-3 py-2">
            <code
              className="text-xs text-zinc-300 truncate flex-1"
              data-testid="podcast-feed-url"
            >
              {state.feed_url}
            </code>
            <button
              type="button"
              onClick={copy}
              data-testid="podcast-feed-copy"
              className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white text-[10px] font-black uppercase tracking-widest"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <a
              href={appleUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="podcast-open-apple"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-white text-[10px] font-black uppercase tracking-widest"
            >
              <ExternalLink className="h-3 w-3" /> Apple Podcasts
            </a>
            <a
              href={overcastUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="podcast-open-overcast"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-white text-[10px] font-black uppercase tracking-widest"
            >
              <ExternalLink className="h-3 w-3" /> Overcast
            </a>
            <button
              type="button"
              onClick={rotate}
              disabled={rotating}
              data-testid="podcast-feed-rotate"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 text-zinc-300 text-[10px] font-black uppercase tracking-widest"
            >
              {rotating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              New URL
            </button>
          </div>

          <p className="text-[11px] text-zinc-600 mt-3 leading-snug">
            Keep it to yourself - this link is tied to your account, and sharing it counts as account
            sharing under the subscriber terms.
          </p>
          {state.voice_disclaimer && (
            <p className="text-[11px] text-zinc-600 mt-2 leading-snug" data-testid="podcast-voice-disclaimer">
              {state.voice_disclaimer}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PodcastFeedCard;
